/*
 * BOGOF cart nudge
 * ----------------
 * Injects the offer state into the SlideCart drawer:
 *   odd number of eligible prints  -> "Add 1 more print and it's FREE"
 *   even number of eligible prints -> no banner; the free line itself reads
 *                                     "FREE" with its struck-out price instead
 *
 * The heading sits directly above SlideCart's own upsell block, not in the
 * free-shipping rewards row: the upsell is where the shopper actually picks their
 * next print, right above Checkout. It also relabels those upsell prices, because
 * SlideCart knows nothing about BOGOF and would otherwise advertise a print at full
 * price when adding it would in fact be free.
 *
 * Why this lives in the theme rather than in the SlideCart app:
 * SlideCart's own "rewards" checkpoints are spend-based, and BOGOF is a
 * quantity offer, so the odd/even state cannot be expressed as a spend
 * threshold. The app's rewards row is left alone (it carries free shipping).
 *
 * Three SlideCart constraints shape this file, all learned the hard way
 * elsewhere in this theme:
 *   1. SlideCart owns the drawer. assets/cart-drawer.js:37-40 makes Dawn's
 *      own drawer a no-op, so anything rendered into snippets/cart-drawer.liquid
 *      is never seen. We have to inject into #slidecarthq.
 *   2. SlideCart monkey-patches window.fetch, so cart reads go through a
 *      pristine fetch taken from a hidden iframe. Same technique as
 *      snippets/framing-selector.liquid:500-515 and
 *      sections/mystery-gift-bar.liquid:253-268.
 *   3. SlideCart re-renders its drawer wholesale, so the node has to be
 *      re-injected via MutationObserver rather than added once.
 *
 * Eligibility deliberately matches snippets/offer-badge.liquid and the
 * `bogof-prints` smart collection the discount is entitled to, so the three
 * can never disagree. /cart.js does not expose product tags, so the eligible
 * product-id set is read once per session from the collection's JSON.
 */
(function () {
  'use strict';

  var COLLECTION = 'bogof-prints';
  var CACHE_KEY = 'yp_bogof_ids_v1';
  var NODE_CLASS = 'yp-bogof-nudge';
  var ACCENT = '#788BFF';

  /* ---------- pristine fetch (bypasses SlideCart's patched window.fetch) ---------- */
  var _nativeFetch = null;
  function getNativeFetch() {
    if (_nativeFetch) return Promise.resolve(_nativeFetch);
    return new Promise(function (resolve) {
      var iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = 'about:blank';
      iframe.addEventListener('load', function () {
        _nativeFetch = iframe.contentWindow.fetch.bind(iframe.contentWindow);
        resolve(_nativeFetch);
      });
      document.body.appendChild(iframe);
    });
  }

  /* ---------- eligible product ids ---------- */
  var eligibleIds = null;

  function readCachedIds() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var arr = JSON.parse(raw);
      return Array.isArray(arr) && arr.length ? arr : null;
    } catch (e) {
      return null;
    }
  }

  function getEligibleIds() {
    if (eligibleIds) return Promise.resolve(eligibleIds);
    var cached = readCachedIds();
    if (cached) {
      eligibleIds = new Set(cached);
      return Promise.resolve(eligibleIds);
    }
    return getNativeFetch()
      .then(function (f) {
        return f('/collections/' + COLLECTION + '/products.json?limit=250', {
          headers: { Accept: 'application/json' }
        });
      })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var ids = (j && j.products ? j.products : []).map(function (p) { return p.id; });
        if (!ids.length) return null;
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(ids)); } catch (e) {}
        eligibleIds = new Set(ids);
        return eligibleIds;
      })
      .catch(function () { return null; });
  }

  /* ---------- cart state ---------- */
  function getCart() {
    return getNativeFetch()
      .then(function (f) { return f('/cart.js', { credentials: 'same-origin' }); })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function money(pence) {
    return '£' + (pence / 100).toFixed(2).replace(/\.00$/, '');
  }

  /*
   * Expand eligible lines into one entry per unit, so the offer can be
   * modelled the way Shopify's Buy X Get Y actually behaves: the cheapest
   * qualifying units are the ones given away. Pre-discount prices, because once
   * the discount applies final_price is £0 on the free line, and averaged across
   * the units of a line that is only partly free.
   */
  function summarise(cart, ids) {
    var unitPrices = [];

    (cart.items || []).forEach(function (item) {
      if (!ids.has(item.product_id)) return;
      for (var i = 0; i < item.quantity; i++) unitPrices.push(item.original_price);
    });

    unitPrices.sort(function (a, b) { return a - b; });

    var count = unitPrices.length;
    var free = Math.floor(count / 2);

    /*
     * With an odd count, one more print tips a new pair into the offer. Because
     * Shopify frees the *cheapest* units, a new print x only costs nothing when it
     * is no dearer than the cheapest unit currently being paid for, which after
     * sorting ascending is unitPrices[free]. Dearer than that, x gets paid for
     * and that unit goes free instead, so the shopper still saves exactly its price.
     */
    var paidFloor = count % 2 === 1 ? unitPrices[free] : null;

    return { count: count, free: free, paidFloor: paidFloor };
  }

  /* ---------- rendering ---------- */
  function buildHTML(state, hasUpsells) {
    /* An even count has nothing left to nudge. What it has earned is shown on the
       free line itself (markLines below) and in SlideCart's own discount row. */
    if (state.count % 2 === 0) return '';

    var headline = 'Add 1 more print and it\'s FREE';
    /* When SlideCart is showing upsells, point at them: they are relabelled FREE
       directly below. */
    var sub = hasUpsells
      ? 'Pick one below, or add any print. The cheapest one is free.'
      : 'Buy one get one free on every print. The cheapest one is free.';

    return (
      '<div style="display:flex;align-items:flex-start;gap:10px;">' +
        '<span aria-hidden="true" style="flex-shrink:0;font-size:16px;line-height:1.3;">&#127873;</span>' +
        '<span style="font-family:CircularXX,sans-serif;font-size:14px;line-height:1.35;color:#121212;">' +
          '<span style="font-family:CircularXXMedium,sans-serif;font-weight:400;">' + headline + '</span><br>' +
          '<span style="opacity:0.75;">' + sub + '</span>' +
        '</span>' +
      '</div>'
    );
  }

  function makeNode() {
    var el = document.createElement('div');
    el.className = NODE_CLASS;
    el.setAttribute('role', 'status');
    /* Reads as the heading of SlideCart's upsell card below it, so no background
       of its own. SlideCart centres text in places; the offer is left-aligned. */
    el.style.cssText = 'padding:12px 30px 8px;text-align:left;';
    return el;
  }

  /* Heading goes directly above SlideCart's upsell block. If SlideCart shows no
     upsells, it sits above the footer so the offer is still visible at Checkout. */
  function placeNode(drawer, node) {
    var upsells = drawer.querySelector('.upsells');
    var anchor = upsells || drawer.querySelector('.footer');
    if (!anchor || !anchor.parentNode) {
      if (node.parentNode !== drawer) drawer.appendChild(node);
      return;
    }
    /* Only move when out of place, so the observer cannot loop on our own move. */
    if (node.nextElementSibling !== anchor) anchor.parentNode.insertBefore(node, anchor);
  }

  function paint(state) {
    var drawer = document.querySelector('#slidecarthq .slidecarthq');
    if (!drawer) return;

    /* SlideCart renders upsells asynchronously, so this can flip after the first
       paint; the observer re-calls paint() and the signature below picks it up. */
    var hasUpsells = !!drawer.querySelector('.upsell[data-slidecart-product-id]');
    var html = buildHTML(state, hasUpsells);
    var existing = drawer.querySelector('.' + NODE_CLASS);

    if (!html) {
      if (existing) existing.remove();
      return;
    }

    var signature = state.count + '|' + (hasUpsells ? 'u' : '-');
    var node = existing || makeNode();

    /* Only rewrite the content when something changed. Together with the
       placement and relabel guards, this keeps the observer from looping. */
    if (node.dataset.signature !== signature) {
      node.dataset.signature = signature;
      node.innerHTML = html;
    }
    placeNode(drawer, node);
  }

  /* ---------- upsell relabelling ---------- */

  function parsePence(text) {
    var m = String(text || '').replace(/,/g, '').match(/(\d+(?:\.\d{1,2})?)/);
    return m ? Math.round(parseFloat(m[1]) * 100) : null;
  }

  /*
   * SlideCart renders this DOM with a virtual-DOM framework, so we never write into
   * its text nodes. Doing so detaches them: SlideCart's later updates (the price
   * changing when a size is picked, for instance) would land on the detached node and
   * the visible label would freeze on "FREE" even for a dearer size. So SlideCart's
   * own price element is hidden but left live, our label is rendered beside it, and
   * button labels are swapped with a data attribute plus CSS in layout/theme.liquid.
   * Every write is guarded, so the MutationObserver can re-run this and it settles.
   */
  var SR_ONLY = 'display:inline-block;width:1px;height:1px;overflow:hidden;clip-path:inset(50%);white-space:nowrap;';

  function childOf(host, cls) {
    for (var i = 0; i < host.children.length; i++) {
      if (host.children[i].classList.contains(cls)) return host.children[i];
    }
    return null;
  }

  function setText(el, text) {
    if (el.textContent !== text) el.textContent = text;
  }

  function hide(el) {
    if (el && el.style.display !== 'none') el.style.setProperty('display', 'none', 'important');
  }

  function unhide(el) {
    if (el && el.style.display) el.style.removeProperty('display');
  }

  function relabelPrice(priceEl, compareEl, mode, saving) {
    if (!priceEl || !priceEl.parentNode) return;
    var host = priceEl.parentNode;
    var live = priceEl.textContent.trim(); /* always SlideCart's current price */
    /* SlideCart's own "Original price" screen-reader text belongs to its compare-at,
       so it is hidden whenever that compare-at is. */
    var srOriginal = childOf(host, 'amp-sc__visually-hidden');

    var freeEl = childOf(host, 'yp-bogof-free');
    var wasEl = childOf(host, 'yp-bogof-was');
    var saveEl = childOf(host, 'yp-bogof-save');

    if (mode === 'free') {
      if (!freeEl) {
        freeEl = document.createElement('span');
        freeEl.className = 'yp-bogof-free';
        freeEl.style.cssText = 'color:#111344;font-family:CircularXXMedium,CircularXX,sans-serif;font-weight:400;';
        host.insertBefore(freeEl, priceEl);
      }
      setText(freeEl, 'FREE');

      if (!wasEl) {
        wasEl = document.createElement('span');
        wasEl.className = 'yp-bogof-was';
        wasEl.style.cssText = 'margin-left:6px;';
        wasEl.innerHTML = '<span style="' + SR_ONLY + '">, usually </span>' +
          '<span class="yp-bogof-was__price" style="text-decoration:line-through;opacity:0.55;"></span>';
        host.insertBefore(wasEl, priceEl);
      }
      setText(wasEl.querySelector('.yp-bogof-was__price'), live);

      hide(priceEl);
      hide(compareEl);
      hide(srOriginal);
      if (saveEl) saveEl.remove();
      return;
    }

    if (freeEl) freeEl.remove();
    if (wasEl) wasEl.remove();
    unhide(priceEl);
    unhide(compareEl);
    unhide(srOriginal);

    if (mode === 'save') {
      if (!saveEl) {
        saveEl = document.createElement('span');
        saveEl.className = 'yp-bogof-save';
        saveEl.style.cssText = 'display:block;color:#111344;font-size:0.85em;margin-top:2px;';
        host.appendChild(saveEl);
      }
      setText(saveEl, 'Saves ' + money(saving));
    } else if (saveEl) {
      saveEl.remove();
    }
  }

  function relabelButton(btn, label) {
    if (!btn) return;
    if (!label) {
      if (btn.hasAttribute('data-yp-label')) btn.removeAttribute('data-yp-label');
      return;
    }
    /* Measure the real font size before the CSS zeroes it for the swap. */
    if (!btn.hasAttribute('data-yp-fs')) {
      var fs = getComputedStyle(btn).fontSize;
      btn.setAttribute('data-yp-fs', fs);
      btn.style.setProperty('--yp-fs', fs);
    }
    if (btn.getAttribute('data-yp-label') !== label) btn.setAttribute('data-yp-label', label);
  }

  function modeFor(state, ids, productId, pence) {
    if (!state || state.paidFloor === null || state.count % 2 === 0) return 'full';
    if (!ids || !productId || !ids.has(productId) || pence === null) return 'full';
    return pence <= state.paidFloor ? 'free' : 'save';
  }

  /* The size dialog carries no product id of its own, only the product title as its
     aria-label, so resolve it through the matching card. No match means no claim. */
  function productIdByTitle(drawer, title) {
    var cards = drawer.querySelectorAll('.upsell[data-slidecart-product-id]');
    for (var i = 0; i < cards.length; i++) {
      var t = cards[i].querySelector('[data-testid="AupsellTitle"]');
      if (t && t.textContent.trim() === title) {
        return Number(cards[i].getAttribute('data-slidecart-product-id'));
      }
    }
    return null;
  }

  function applyUpsellLabels(state, ids) {
    var drawer = document.querySelector('#slidecarthq .slidecarthq');
    if (!drawer) return;
    var saving = state ? state.paidFloor : null;

    /* Upsell cards. */
    drawer.querySelectorAll('.upsell[data-slidecart-product-id]').forEach(function (card) {
      var priceEl = card.querySelector('[data-testid="AupsellPrice"]');
      if (!priceEl) return;
      var pid = Number(card.getAttribute('data-slidecart-product-id'));
      var mode = modeFor(state, ids, pid, parsePence(priceEl.textContent));
      relabelPrice(priceEl, card.querySelector('.upsell-compare-at'), mode, saving);
      relabelButton(card.querySelector('.upsell-add button'), mode === 'free' ? 'Add free' : null);
    });

    /* Size dialogs. These sit beside the cards inside .upsells, not inside them, and
       their price follows the chosen size, so each is judged on its live price. */
    drawer.querySelectorAll('.upsell-options').forEach(function (dialog) {
      var prices = dialog.querySelector('.upsell-options-prices');
      var priceEl = prices && prices.querySelector('p.price');
      if (!priceEl) return;
      var pid = productIdByTitle(drawer, (dialog.getAttribute('aria-label') || '').trim());
      var mode = modeFor(state, ids, pid, parsePence(priceEl.textContent));
      relabelPrice(priceEl, prices.querySelector('p.compare-at'), mode, saving);
      dialog.querySelectorAll('button').forEach(function (b) {
        if (b.classList.contains('upsell-options-close')) return;
        relabelButton(b, mode === 'free' ? 'Add free print' : null);
      });
    });
  }

  /* ---------- free-line marking ---------- */

  /*
   * The BOGOF allocation is the 100%-off one on an eligible print. Matching on that
   * rather than on a title means the staging test code and the launch discount are
   * both recognised, while WELCOME10 or a bundle discount on the same line is not.
   * Shopify also attaches a £0 allocation to the paid line of the pair, so this
   * returns those too: their tag gets hidden, they just don't get a label.
   */
  function bogofAllocations(item, ids) {
    if (!ids.has(item.product_id)) return [];
    return (item.line_level_discount_allocations || []).filter(function (a) {
      var app = a.discount_application || {};
      return app.value_type === 'percentage' && parseFloat(app.value) === 100;
    });
  }

  /*
   * Same rule as relabelPrice: SlideCart's nodes are hidden or unhidden, never
   * written into, and every write is guarded so the observer settles.
   *
   * SlideCart's own rendering of the row is the tie-breaker. When both prints cost
   * the same, Shopify can move the free allocation between them from one cart read
   * to the next, and SlideCart also reuses row elements across re-renders. Marking
   * from cart data alone once put "FREE" on the paid line while SlideCart showed
   * £0.00 on the other. So a line is only marked when SlideCart shows it discounted.
   */
  function markLine(row, item, allocs) {
    var titles = allocs.map(function (a) { return a.discount_application.title; });
    var saved = allocs.reduce(function (s, a) { return s + (a.amount || 0); }, 0);
    var block = row.querySelector('.price-discount-block');
    var zero = block && block.querySelector('.price:not(.strike-through)');
    var shownDiscounted = !!(block && block.querySelector('.price.strike-through'));
    var shownFree = shownDiscounted && !!zero && parsePence(zero.textContent) === 0;
    var unitsFree = shownDiscounted && item.original_price ? Math.round(saved / item.original_price) : 0;
    var allFree = saved > 0 && item.final_line_price === 0 && shownFree;

    /* SlideCart tags each line with the discount's name. The BOGOF tag is swapped for
       the label below; any other discount's tag is left showing. */
    row.querySelectorAll('.discount-allocations').forEach(function (tag) {
      var name = tag.querySelector('.discount-allocations-flex-1');
      if (name && titles.indexOf(name.textContent.trim()) !== -1) hide(tag);
      else unhide(tag);
    });

    var left = row.querySelector('.main-top-left');
    var label = left && childOf(left, 'yp-bogof-line');
    if (left && unitsFree > 0) {
      if (!label) {
        label = document.createElement('span');
        label.className = 'yp-bogof-line';
        label.style.cssText = 'display:block;margin-top:4px;font-size:0.85em;color:#111344;';
        left.appendChild(label);
      }
      setText(label, unitsFree === item.quantity
        ? 'Buy one get one free'
        : unitsFree + ' of ' + item.quantity + ' free');
    } else if (label) {
      label.remove();
    }

    /* A wholly free line reads "FREE £11.00" (struck) rather than SlideCart's
       "£11.00 £0.00". A partly free line keeps SlideCart's struck and reduced prices. */
    /* Report when SlideCart shows this line free but our cart data says otherwise,
       so the caller can fetch a fresh cart. */
    var disagrees = shownFree && item.final_line_price !== 0;
    if (!block) return disagrees;
    var freeEl = childOf(block, 'yp-bogof-free');
    if (allFree) {
      if (!freeEl) {
        freeEl = document.createElement('span');
        freeEl.className = 'yp-bogof-free';
        freeEl.style.cssText = 'margin-right:6px;color:#111344;font-family:CircularXXMedium,CircularXX,sans-serif;font-weight:400;';
        freeEl.textContent = 'FREE';
      }
      if (block.firstChild !== freeEl) block.insertBefore(freeEl, block.firstChild);
      hide(zero);
    } else {
      if (freeEl) freeEl.remove();
      unhide(zero);
    }
    return disagrees;
  }

  /* Returns true when the drawer shows something our cached cart can't explain: a
     row it doesn't know, or a line SlideCart shows free that it has as paid. */
  function markLines(cart, ids) {
    var drawer = document.querySelector('#slidecarthq .slidecarthq');
    if (!drawer || !cart) return false;
    var byKey = {};
    var stale = false;
    (cart.items || []).forEach(function (item) { byKey[item.key] = item; });
    drawer.querySelectorAll('.item[data-line-item-key]').forEach(function (row) {
      var item = byKey[row.getAttribute('data-line-item-key')];
      if (!item) {
        stale = true;
        return;
      }
      if (markLine(row, item, bogofAllocations(item, ids))) stale = true;
    });
    return stale;
  }

  /* ---------- free-shipping bar ---------- */

  /*
   * Shopify's free UK rate (Settings > Shipping > Domestic, the £0 "Express" rate) is
   * conditioned on the cart total AFTER discounts. SlideCart's rewards bar was set to
   * the total before discounts, so under BOGOF a 2 x A4 cart (£30 before, £15 after)
   * read "FREE Shipping applied!" and checkout then charged £3.95.
   *
   * The proper fix is two SlideCart app settings (rewards on the final total, and the
   * free-shipping tier amount). Until those are changed, and as a backstop after, the
   * same two values are enforced on SlideCart's live settings object. If SlideCart
   * changes that object's shape, this does nothing and the app's settings apply.
   * FREE_SHIPPING_FROM must match the Shopify rate's condition.
   */
  var FREE_SHIPPING_FROM = '35.00';

  /* Returns true when a value had to be corrected, so the caller can re-render. */
  function syncShippingBar() {
    var settings;
    try { settings = window.SLIDECART_STATE().settings; } catch (e) { return false; }
    if (!settings || !Array.isArray(settings.rewards_tiers)) return false;
    var changed = false;
    if (settings.rewards_final_total !== true) {
      settings.rewards_final_total = true;
      changed = true;
    }
    settings.rewards_tiers.forEach(function (tier) {
      if (tier && tier.rewards_type === 'free_shipping' && tier.amount !== FREE_SHIPPING_FROM) {
        tier.amount = FREE_SHIPPING_FROM;
        changed = true;
      }
    });
    return changed;
  }

  /* SlideCart builds its state after this script loads, so try for a few seconds;
     correcting it before the drawer is first opened avoids a flash of the wrong bar. */
  function syncShippingBarOnLoad() {
    var tries = 0;
    (function attempt() {
      if (typeof window.SLIDECART_STATE === 'function') {
        try {
          if (window.SLIDECART_STATE().settings) {
            syncShippingBar();
            return;
          }
        } catch (e) {}
      }
      if (++tries < 40) setTimeout(attempt, 250);
    })();
  }

  /* ---------- frame nudge ---------- */

  /*
   * "Add a frame for free delivery", directly under the shipping bar, offered only
   * when ONE frame closes the gap to FREE_SHIPPING_FROM and there's an unframed
   * print of that size in the cart. Under BOGOF, adding another print to an odd
   * count adds £0, so for the most common near-miss baskets (3-4 x A4 at £30,
   * 1-2 x A3 at £22) a frame is the smallest thing that actually reaches the
   * threshold. A basket no single frame can rescue (1-2 x A4 at £15) gets nothing.
   *
   * The frame is added the way the PDP adds it (snippets/framing-selector.liquid):
   * its own line, with `_frame_for` naming the print so the packer knows which print
   * to frame. Prices and variant ids come from the frame product's JSON, not
   * constants, so a frame repricing can't make this advertise the wrong amount.
   */
  var FRAMES_HANDLE = 'frames';
  var FRAME_NODE_CLASS = 'yp-frame-nudge';
  var framesData = null;
  var framesLoading = null;

  function sizeOf(text) {
    var m = String(text || '').match(/\bA([234])\b/);
    return m ? 'A' + m[1] : null;
  }

  function getFrames() {
    if (framesData) return Promise.resolve(framesData);
    if (framesLoading) return framesLoading;
    framesLoading = getNativeFetch()
      .then(function (f) { return f('/products/' + FRAMES_HANDLE + '.js', { headers: { Accept: 'application/json' } }); })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p) return null;
        var bySize = {};
        (p.variants || []).forEach(function (v) {
          var s = sizeOf(v.title);
          if (!s || !v.available) return;
          bySize[s] = { id: v.id, price: v.price };
        });
        framesData = { productId: p.id, bySize: bySize };
        return framesData;
      })
      .catch(function () { return null; })
      .then(function (d) { framesLoading = null; return d; });
    return framesLoading;
  }

  function frameOffer(cart, ids, frames) {
    if (!cart || !ids || !frames) return null;
    var threshold = Math.round(parseFloat(FREE_SHIPPING_FROM) * 100);
    var total = cart.total_price;
    if (!(total > 0) || total >= threshold) return null;

    /* Per size: the prints in the cart, minus one per frame already bought for that
       size (matched on `_frame_for` where possible), leaves the unframed ones. */
    var prints = {};
    var framedFor = {};
    (cart.items || []).forEach(function (item) {
      var s = sizeOf(item.variant_title);
      if (!s) return;
      var n = item.quantity;
      if (item.product_id === frames.productId) {
        var forTitle = (item.properties && item.properties._frame_for) || '';
        framedFor[s] = (framedFor[s] || []).concat(new Array(n).fill(forTitle));
      } else if (ids.has(item.product_id)) {
        prints[s] = (prints[s] || []).concat(new Array(n).fill({ title: item.product_title, image: item.image }));
      }
    });

    var best = null;
    Object.keys(prints).forEach(function (s) {
      var frame = frames.bySize[s];
      if (!frame || total + frame.price < threshold) return;
      var unframed = prints[s].slice();
      (framedFor[s] || []).forEach(function (t) {
        var i = unframed.map(function (u) { return u.title; }).indexOf(t);
        unframed.splice(i === -1 ? 0 : i, 1);
      });
      if (!unframed.length) return;
      if (!best || frame.price < best.price) {
        best = { size: s, variantId: frame.id, price: frame.price, image: unframed[0].image, forTitle: unframed[0].title };
      }
    });
    return best;
  }

  function addFrame(button, offer) {
    button.disabled = true;
    button.textContent = 'Adding';
    getNativeFetch()
      .then(function (f) {
        return f('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: offer.variantId, quantity: 1, properties: { '_frame_for': offer.forTitle } }] })
        });
      })
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        if (typeof window.SLIDECART_UPDATE === 'function') {
          try { window.SLIDECART_UPDATE(); } catch (e) {}
        }
        refresh();
      })
      .catch(function () {
        button.disabled = false;
        button.textContent = 'Try again';
      });
  }

  function imageUrl(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* Sits right under SlideCart's shipping bar, in the same grey band, so it reads as
     the answer to "Spend £X more". The thumbnail is the shopper's own print in a black
     frame, which sells the frame better than a stock frame shot. The Add button is black
     like SlideCart's own upsell button: green is kept for Checkout. Every element sets
     its own font-size, because SlideCart's reset gives `.slidecarthq *` font-size: medium. */
  function paintFrameNudge(offer) {
    var drawer = document.querySelector('#slidecarthq .slidecarthq');
    if (!drawer) return;
    var node = drawer.querySelector('.' + FRAME_NODE_CLASS);
    var anchor = drawer.querySelector('.rewards');
    if (!offer || !anchor || !anchor.parentNode) {
      if (node) node.remove();
      return;
    }

    var signature = offer.variantId + '|' + offer.price + '|' + offer.forTitle;
    if (!node) {
      node = document.createElement('div');
      node.className = FRAME_NODE_CLASS;
      node.style.cssText = 'display:flex;align-items:center;gap:12px;padding:4px 30px 12px;margin-top:-1px;background:rgba(0,0,0,0.05);';
    }
    if (node.dataset.signature !== signature) {
      node.dataset.signature = signature;
      var name = offer.forTitle.replace(/ - Print$/, '');
      var price = money(offer.price);
      node.innerHTML =
        (offer.image
          ? '<img src="' + imageUrl(offer.image, 120) + '" alt="" width="36" height="46" style="flex-shrink:0;width:36px;height:46px;object-fit:cover;padding:2px;background:#fff;border:3px solid #121212;box-sizing:content-box;">'
          : '') +
        '<span style="flex:1;min-width:0;">' +
          '<span style="display:block;font-family:CircularXXMedium,sans-serif;font-weight:400;font-size:14px;line-height:1.3;color:#121212;">Get free delivery</span>' +
          '<span class="' + FRAME_NODE_CLASS + '__sub" style="display:block;margin-top:1px;font-family:CircularXX,sans-serif;font-size:13px;line-height:1.35;color:#121212;opacity:0.75;"></span>' +
        '</span>' +
        '<button type="button" style="flex-shrink:0;padding:9px 16px;border:0;border-radius:4px;background:#121212;color:#fff;font-family:CircularXXMedium,sans-serif;font-weight:400;font-size:12px;line-height:1;letter-spacing:0.06em;text-transform:uppercase;cursor:pointer;">Add</button>';
      node.querySelector('.' + FRAME_NODE_CLASS + '__sub').textContent =
        'Add a black ' + offer.size + ' frame for ' + name + ', ' + price;
      var button = node.querySelector('button');
      button.setAttribute('aria-label', 'Add a black ' + offer.size + ' frame for ' + name + ', ' + price + ', and get free delivery');
      button.addEventListener('click', function () { addFrame(button, offer); });
    }
    /* Only move when out of place, so the observer cannot loop on our own move. */
    if (anchor.nextElementSibling !== node) anchor.parentNode.insertBefore(node, anchor.nextSibling);
  }

  function renderFrameNudge() {
    if (!framesData || !lastCart || !eligibleIds) return;
    paintFrameNudge(frameOffer(lastCart, eligibleIds, framesData));
  }

  /* ---------- refresh ---------- */

  var lastState = null;
  var lastCart = null;
  var refreshing = false;
  var refreshAgain = false;

  /* A call that lands mid-flight is queued, not dropped: the in-flight read may
     predate the change that prompted this call (the free pick being added right
     after the main print, say), and dropping it left the drawer on stale data. */
  function refresh() {
    if (refreshing) {
      refreshAgain = true;
      return;
    }
    refreshing = true;
    Promise.all([getEligibleIds(), getCart()])
      .then(function (res) {
        var ids = res[0];
        var cart = res[1];
        if (!ids || !cart) return;
        lastCart = cart;
        lastState = summarise(cart, ids);
        paint(lastState);
        applyUpsellLabels(lastState, ids);
        /* Our read can itself be older than what SlideCart has rendered. */
        catchUp(markLines(cart, ids));
        return getFrames().then(renderFrameNudge);
      })
      .catch(function () {})
      .then(function () {
        refreshing = false;
        if (refreshAgain) {
          refreshAgain = false;
          refresh();
        }
      });
  }

  /* ---------- wiring ---------- */

  /* SlideCart re-renders its drawer, and swaps upsell prices when a size is picked,
     so re-assert on every subtree change. Everything the callback does is
     guarded to write only when out of date, so it settles instead of looping. */
  /*
   * Catch up when the drawer shows a cart we haven't read. When both prints cost the
   * same, Shopify can move the free allocation between them from one cart read to
   * the next, and each move changes the lines' keys, so SlideCart can be showing a
   * newer cart than our last read. Scheduled rather than throttled-and-dropped: a
   * dropped catch-up is never retried once the drawer stops changing. Capped so a
   * cart that never settles can't loop.
   */
  var staleTimer = null;
  var staleTries = 0;

  function catchUp(stale) {
    if (!stale) {
      staleTries = 0;
      return;
    }
    if (staleTimer || staleTries >= 5) return;
    staleTries++;
    staleTimer = setTimeout(function () {
      staleTimer = null;
      refresh();
    }, 800);
  }

  function onDrawerMutation() {
    var drawer = document.querySelector('#slidecarthq .slidecarthq');
    if (!drawer) return;
    if (lastState && eligibleIds) {
      /* paint() re-places the heading and updates its copy once SlideCart's
         async upsells arrive; it only writes when the signature changed. */
      if (drawer.querySelector('.' + NODE_CLASS)) paint(lastState);
      applyUpsellLabels(lastState, eligibleIds);
      catchUp(markLines(lastCart, eligibleIds));
      renderFrameNudge();
    }
    /* Only odd counts carry the heading, so only they can have lost it. */
    if (lastState && lastState.count % 2 === 1 && !drawer.querySelector('.' + NODE_CLASS)) refresh();
  }

  function touchesDrawer(mutation) {
    var t = mutation.target;
    var el = t.nodeType === 1 ? t : t.parentElement;
    if (el && el.closest('#slidecarthq')) return true;
    for (var i = 0; i < mutation.addedNodes.length; i++) {
      var n = mutation.addedNodes[i];
      if (n.nodeType === 1 && (n.id === 'slidecarthq' || n.querySelector('#slidecarthq'))) return true;
    }
    return false;
  }

  function start() {
    /*
     * Observed from <body>, filtered to the drawer, rather than on #slidecarthq
     * itself: SlideCart swaps that element after load, which left an observer on it
     * watching a detached node. The drawer then kept whatever the last refresh
     * marked, and a "FREE" stayed on a row SlideCart had re-rendered as paid.
     */
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (touchesDrawer(mutations[i])) {
          onDrawerMutation();
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
    refresh();

    /* SlideCart's own lifecycle callbacks. Chain rather than overwrite:
       snippets/framing-selector.liquid and assets/bogof-picker.js hook them too. */
    syncShippingBarOnLoad();

    ['SLIDECART_UPDATED', 'SLIDECART_OPENED'].forEach(function (name) {
      var prev = window[name];
      window[name] = function () {
        if (typeof prev === 'function') {
          try { prev.apply(this, arguments); } catch (e) {}
        }
        /* Only re-render when a value was actually corrected, so this can't loop. */
        if (syncShippingBar() && typeof window.SLIDECART_UPDATE === 'function') {
          try { window.SLIDECART_UPDATE(); } catch (e) {}
        }
        refresh();
      };
    });

    /* Dawn's own cart pub/sub, for the /cart page and any non-SlideCart path. */
    document.addEventListener('cart:refresh', refresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
